<template>
  <div
    v-if="standard != null"
    :key="standard.id"
    class="paper-block-container paper-block-container--main"
  >
    <div class="paper__top">
      <div class="paper__header">
        <div>
          <InputTitle
            id="block-name"
            v-model="standard.name"
            iconName="star"
            :maxLength="200"
            @save="saveName"
          />

          <div class="pt-1">
            <span class="mr-3">Standard</span>

            <span class="span-date mr-3" v-html="new Date(standard.created).toLocaleDateString()" />

            <StateArchived v-if="standard.archived" />
          </div>
        </div>
        <div class="standard-component__button-group">
          <Btn
            v-if="standard.archived"
            type="primary-transparent"
            icon-left="restore"
            padding="xs"
            height="s"
            class="standard-component__action_button"
            @click="restore"
          >
            Restore
          </Btn>
          <button v-else-if="isEditMode" class="button--square-image" @click="finishEditing">
            <i class="material-icons material-icon--18">done</i>
            Finish <br />
            editing
          </button>

          <div v-show="!standard.archived && !isEditMode" class="standard-component__button-column">
            <Btn
              type="primary-transparent"
              icon-left="edit"
              padding="xs"
              height="s"
              class="standard-component__action_button"
              @click="startEditing"
            >
              Edit
            </Btn>
            <div v-if="isPaused" v-tippy content="Standard is paused! Stop it to archive!">
              <Btn
                type="primary-transparent"
                icon-left="delete"
                padding="xs"
                height="s"
                class="standard-component__action_button"
                data-test-id="btn-archive"
                disabled
              >
                Archive
              </Btn>
            </div>
            <Btn
              v-else-if="standard.can_archive && !isInProgress"
              type="primary-transparent"
              icon-left="delete"
              padding="xs"
              height="s"
              class="standard-component__action_button"
              @click="archive"
            >
              Archive
            </Btn>
          </div>
        </div>
      </div>
      <div class="paper__content--margin-32">
        <ModelTextareaComponent
          ref="description"
          v-model="standard.description"
          :canEdit="standard.can_edit"
          :isEditMode="isEditMode"
          :label="'Description'"
          style="margin: 16px 0 10px"
          @click="startEditing"
          @update:value="saveDescription"
        />
        <div>
          <label class="label-block-margin" style="margin: 16px 0 0"> Compound </label>
          <div>{{ compoundName || 'No data' }}</div>
        </div>
        <div>
          <label class="label-block-margin" style="margin: 16px 0 0">Standard concentration</label>
          <div>
            <template v-if="calibrationConcentration != null">
              <span>{{ calibrationConcentration }}</span>
              <span v-if="calibrationUnit"> {{ calibrationUnit }}</span>
            </template>
            <i v-else class="text-gray">No data</i>
          </div>
        </div>
        <br />

        <Btn
          type="primary-transparent"
          icon-left="visibility"
          padding="xs"
          height="s"
          class="standard-component__action_button"
          :isLoading="isCalibrationIdLoading"
          @click="goToCalibration"
        >
          Show calibration
        </Btn>
      </div>
    </div>

    <!--I change v-show to v-if, because this component conflicts with the Chart component on the calibration page.
      v-show doesn't work in case of peak highlighting -->
    <div v-if="injectionsAll">
      <StandardInjectionsListComponent
        :archived="showArchivedInjections"
        :standard="standard"
        :class="{ 'block--shadow-in': !empty && standard.k }"
        :injectionID="injectionID"
        :injections="injections"
        :injectionHighlightedId.sync="injectionHighlightedId"
        :isInProgress="isInProgress"
        :calibrationConcentration="calibrationConcentration"
        :calibrationUnit="calibrationUnit"
        @update:archived="showArchivedInjections = $event"
        @update:injectionID="showInjection($event)"
      />
    </div>
    <div v-show="!injectionsAll" style="padding-bottom: 32px">
      <InjectionsNavBarComponent
        :archived="showArchivedInjections"
        :injectionID="injectionID"
        :injections="injections"
        :allowNew="false"
        :isInProgress="isInProgress"
        @update:archived="showArchivedInjections = $event"
        @update:injectionID="showInjection($event)"
      />

      <StandardInjectionComponent
        v-if="injectionData"
        ref="injection"
        :standard="standard"
        :injectionID="injectionID"
        :injection-data="injectionData"
        :calibrationUnit="calibrationUnit"
      />
      <NotAvailableComponent v-else>No data</NotAvailableComponent>
    </div>
  </div>
  <ErrorComponent v-else-if="errorCode != null" :code="errorCode" class="mt-16" />
  <LoadingComponent v-else />
</template>

<script>
  import 'assets/css/base/table.scss';
  import _ from 'lodash';

  import StateArchived from '@/uikitProject/states/StateArchived';
  import StandardInjectionComponent from 'components/block/StandardInjectionComponent';
  import StandardInjectionsListComponent from 'components/block/StandardInjectionsListComponent';
  import StandardSocket, { StandardSocketEvents } from 'api/sockets/StandardSocket';
  import ErrorComponent from 'components/element/ErrorComponent';
  import InjectionsNavBarComponent from 'components/block/InjectionsNavBarComponent';
  import LoadingComponent from 'components/element/LoadingComponent';
  import ModelTextareaComponent from 'components/element/ModelTextareaComponent';
  import Btn from '@/uikitBase/btns/Btn';
  import NotAvailableComponent from 'components/element/NotAvailableComponent';
  import InputTitle from '@/uikitProject/inputs/inputTitle/InputTitle';
  import { apiStandards } from '@/api/graphql/cloud/standards';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { isSampleInProgress } from '@/utils/sampleHelpers';
  import DataNotFoundError from '@/errors/DataNotFoundError';
  import { apiSamples } from '@/api/graphql/cloud/samples';
  import { apiCalibrations } from '@/api/graphql/cloud/calibrations';
  import { formatPreciseNumber } from '@/utils/formatters.ts';

  const EVENT_UPDATE_INJECTION_ID = 'update:injectionID';
  const EVENT_UPDATE_STANDARD_DATA = 'update:standardData';
  const EVENT_EDIT = 'edit';
  const EVENT_ARCHIVE = 'archive';

  export default {
    name: 'StandardComponent',

    components: {
      InputTitle,
      ErrorComponent,
      NotAvailableComponent,
      StandardInjectionsListComponent,
      InjectionsNavBarComponent,
      LoadingComponent,
      ModelTextareaComponent,
      Btn,
      StateArchived,
      StandardInjectionComponent,
    },

    props: {
      id: Number,
      injectionID: Number,
      standardData: Object,
      isPaused: {
        type: Boolean,
      },
    },

    data() {
      return {
        standard: null,
        device: null,
        method: null,
        column: null,
        injections: {},
        injectionData: null,
        standardSocket: this.createSocket(this.id),
        showArchivedInjections: false,
        errorCode: null,
        calibrationLevel: null,

        injectionHighlightedId: null,

        listenersGroupId: null,
        subscriptionForInjectionsState: null,
        subscriptionForStandardBrief: null,

        isEditMode: false,
        wasEditingStated: false,
        isCalibrationIdLoading: false,
      };
    },

    computed: {
      injectionsAll: {
        get() {
          return this.injectionID == null;
        },
        set(b) {
          if (b) this.showInjection(null);
        },
      },
      empty() {
        if (!this.injections) return true;

        const isFound = Object.keys(this.injections).find(
          (key) => this.archived || !this.injections[key].injection.archived,
        );
        return !isFound;
      },

      isInProgress() {
        return isSampleInProgress(this.standard);
      },

      hasInjections() {
        return Object.values(this.injections).length > 0;
      },
      calibrationConcentration() {
        const value = this.calibrationLevel?.nominal_concentration;
        if (value == null) {
          return null;
        }
        return formatPreciseNumber(value);
      },
      calibrationUnit() {
        return this.calibrationLevel?.unit ?? null;
      },
      compoundName() {
        if (
          this.calibrationLevel &&
          this.calibrationLevel.calibration &&
          this.calibrationLevel.calibration.compound
        ) {
          return this.calibrationLevel.calibration.compound.name;
        }
        return null;
      },
    },

    watch: {
      id(id) {
        this.errorCode = null;
        this.standardSocket = this.createSocket(id);
        this.initData(id);
        this.initSubscriptions(id);
      },
      injectionID() {
        if (this.injectionID) {
          this.updateInjectionData();
        }
      },
      injections(injectionsNew) {
        this.updateInjectionData();

        const injectionList = Object.values(injectionsNew);
        if (!injectionList.length && !this.wasEditingStated) {
          this.startEditing();
        } else if (injectionList.length) {
          this.finishEditing();
        }
      },
      standardData(newData) {
        if (newData == null) return;
        if (newData.id !== this.id) return;
        if (this.standard == null || this.standard.id !== newData.id) {
          this.standard = newData;
        }
      },
    },

    created() {
      this.initData(this.id);
      this.initSubscriptions(this.id);
    },

    beforeDestroy() {
      this.destroySocket();
      this.stopSubscriptions();
    },

    methods: {
      async initData(standardId) {
        try {
          await this.loadCalibrationLevel(standardId);
          const data = await apiStandards.getStandardFull(standardId);

          if (_.has(data, 'standard')) {
            this.setStandard(data.standard);
          }
          if (_.has(data, 'device')) {
            this.device = data.device;
          }
          if (_.has(data, 'method')) {
            this.method = data.method;
          }
          if (_.has(data, 'column')) {
            this.column = data.column;
          }
          if (_.has(data, 'injections')) {
            this.injections = data.injections;
          }
        } catch (e) {
          if (e instanceof DataNotFoundError) {
            this.resetData();
          }
          throw e;
        }
      },

      resetData() {
        this.setStandard(undefined);
        this.device = undefined;
        this.method = undefined;
        this.column = undefined;
        this.injections = {};
      },

      setStandard(c) {
        // eslint-disable-next-line vue/no-mutating-props
        this.standardData = {
          ...this.standardData,
          ...c,
        };
        this.standard = this.standardData;
        this.$emit(EVENT_UPDATE_STANDARD_DATA, c);
      },

      destroySocket() {
        if (this.standardSocket) {
          this.standardSocket.close(this.listenersGroupId);
          this.standardSocket = null;
        }
      },
      createSocket(id) {
        this.destroySocket();

        const onStandardRefresh = (standardID) => {
          this.initData(standardID);
          this.$refs.injection.resetZoom();
        };

        const standardSocket = StandardSocket.start(id, null, (e) => {
          this.errorCode = e.code;
        });

        const listenersGroup = standardSocket.createEventListenersGroup();
        this.listenersGroupId = listenersGroup.id;

        listenersGroup.addEventListener(StandardSocketEvents.STANDARD_REFRESH, onStandardRefresh);

        return standardSocket;
      },

      initSubscriptions(standardId) {
        this.stopSubscriptions();
        this.initSubscriptionForInjectionsState(standardId);
        this.initSubscriptionForStandardBrief(standardId);
      },

      initSubscriptionForStandardBrief(standardId) {
        this.subscriptionForStandardBrief = apiSamples.subscribeForSampleBrief(
          standardId,
          (standard) => {
            this.setStandard(standard);
          },
        );
      },

      initSubscriptionForInjectionsState(standardId) {
        this.subscriptionForInjectionsState = apiInjections.subscribeForInjectionsState(
          standardId,
          (injections) => {
            let hasUpdates = false;
            injections.forEach((injectionUpdate) => {
              const existing = this.injections[injectionUpdate.id];

              if (existing) {
                this.injections[injectionUpdate.id] = {
                  ...existing,
                  injection: {
                    ...existing.injection,
                    state: injectionUpdate.state,
                  },
                };
                hasUpdates = true;
              }
            });

            if (hasUpdates) {
              this.updateInjectionData();
            }
          },
        );
      },

      stopSubscriptions() {
        this.subscriptionForInjectionsState?.unsubscribe();
        this.subscriptionForStandardBrief?.unsubscribe();
      },

      updateInjectionData() {
        if (this.injections[this.injectionID] != null) {
          this.injectionData = this.injections[this.injectionID];
        }
      },

      saveName(name) {
        this.showNotificationIfRpcError(() => this.standardSocket.update({ name }));
      },
      saveDescription() {
        this.showNotificationIfRpcError(() =>
          this.standardSocket.update({ description: this.standard.description }),
        );
      },

      archive() {
        this.$modal.show('dialog', {
          title: 'Archive standard?',
          text:
            'When you archive a standard, the archived data will not be used in calibration calculations.',
          buttons: [
            {
              title: 'Archive',
              default: true,
              handler: () => {
                this.$modal.hide('dialog');
                this.showNotificationIfRpcError(() => this.standardSocket.archive());
                this.$emit(EVENT_ARCHIVE);
              },
              class: 'vue-dialog-button blue-text',
            },
            {
              title: 'Cancel',
              handler: () => this.$modal.hide('dialog'),
              class: 'vue-dialog-button',
            },
          ],
        });
      },
      restore() {
        this.showNotificationIfRpcError(() => this.standardSocket.restore());
      },

      showInjection(iid) {
        this.$emit(EVENT_UPDATE_INJECTION_ID, iid);
      },

      startEditing() {
        this.wasEditingStated = true;

        if (this.standard?.can_edit) {
          this.isEditMode = true;
        }
      },
      finishEditing() {
        if (this.isEditMode) {
          this.$refs.description?.save();
          this.isEditMode = false;
          this.$emit(EVENT_EDIT);
        }
      },
      async loadCalibrationLevel(standardId) {
        if (!standardId) {
          this.calibrationLevel = null;
          return;
        }
        try {
          this.calibrationLevel = await apiCalibrations.getCalibrationLevelByStandardId(standardId);
        } catch (error) {
          this.calibrationLevel = null;
        }
      },
      async goToCalibration() {
        if (!this.standard?.id) {
          console.warn('Standard ID not found');
          return;
        }

        const sequenceId = this.standard.sequence_id;
        if (!sequenceId) {
          console.warn('Sequence ID not found for standard');
          return;
        }

        // Load calibration ID only when button is pressed
        this.isCalibrationIdLoading = true;
        try {
          const calibrationLevel = await apiCalibrations.getCalibrationLevelByStandardId(
            this.standard.id,
          );
          const calibrationId = calibrationLevel?.calibration?.id;

          if (!calibrationId) {
            console.warn('Calibration ID not found for standard');
            return;
          }

          this.$router.push({
            name: 'sequence calibrations',
            params: {
              id: sequenceId,
              calibrationId,
            },
          });
        } catch (error) {
          console.warn('Failed to load calibration ID', error);
        } finally {
          this.isCalibrationIdLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .standard-component {
    &__calibration {
      display: flex;
      flex-direction: column;
    }

    &__calibration-value {
      margin-top: 8px;
      padding: 10px 12px;
      border: 1px solid black;
      border-radius: 6px;
      min-height: 40px;
    }

    &__button-group {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__button-column {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__action_button {
      justify-content: start;
    }
  }
</style>
